import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Phone, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { addToBundle } from "./BundleBuilder";

const TOOLKITS = {
  first_steps: ["Come As You Are · First Steps", "Level 1 · Beginner. Gentle first steps for when you're just starting to look at your drinking or using.", "card-first-steps"],
  building_steadiness: ["Unsteady · Building Steadiness", "Level 2 · Intermediate. For when you've started, but the ground still shifts under you.", "card-building-steadiness"],
  more_support: ["Heavy · When Everything Feels Too Much", "Level 3 · Advanced. For the heaviest stretches, when you need more structure and support.", "card-more-support"],
  finding_your_footing: ["Finding Your Footing", "For anxiety, depression and the noise in your head. Name what you feel and find your footing.", "card-mental-health"],
  when_life_changes: ["When Life Changes", "For loss, grief and the seasons that knock you flat. One day at a time.", "card-hardship"],
};

const Q = [
  { key: "what", q: "What are you carrying right now?", a: [["addiction", "Drinking, using or another addiction"], ["mind", "Anxiety, depression or what's going on in my head"], ["hardship", "A hard season: loss, grief, a big change"]] },
  { key: "where", q: "How does today feel?", a: [["start", "I'm just starting to face it"], ["slipping", "I've started, but I keep slipping"], ["heavy", "Heavy. Everything feels like too much"], ["unsafe", "I don't feel safe right now"]] },
  { key: "help", q: "What would help most right now?", a: [["read", "Something I can work through on my own"], ["talk", "Someone to talk to"], ["both", "Both"]] },
];

const pick = ({ what, where }) => {
  if (what === "mind") return "finding_your_footing";
  if (what === "hardship") return "when_life_changes";
  return { start: "first_steps", slipping: "building_steadiness", heavy: "more_support" }[where] || "first_steps";
};

const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });

const Crisis = () => (
  <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-5" data-testid="quiz-crisis">
    <p className="text-sm text-rose-100 mb-3">Thank you for telling us. Please reach out to someone right now. You don't have to carry this alone tonight.</p>
    <a href="tel:988" data-testid="quiz-call-988" className="inline-flex items-center gap-2 rounded-full bg-rose-400 text-black px-5 py-2.5 text-sm font-semibold">
      <Phone size={14} /> Call or text 988
    </a>
    <p className="text-xs text-neutral-400 mt-3">988 Suicide & Crisis Lifeline, free and 24/7 in the US. If you're in immediate danger, call 911.</p>
  </div>
);

const Result = ({ ans, close, restart }) => {
  const navigate = useNavigate();
  const id = pick(ans);
  const [title, why, card] = TOOLKITS[id];
  const wantsTalk = ans.help !== "read";
  return (
    <div className="space-y-4" data-testid="quiz-result">
      {ans.where === "unsafe" && <Crisis />}
      <div className="text-xs uppercase tracking-[0.28em] text-teal-300/80">We'd start here</div>
      <div className="rounded-xl border border-teal-400/40 bg-teal-500/5 p-5">
        <div className="font-display text-2xl text-neutral-100" data-testid="quiz-result-title">{title}</div>
        <p className="text-sm text-neutral-400 mt-1">{why}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" onClick={() => { close(); addToBundle(id); }} data-testid="quiz-add-toolkit" className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2">
            Add to my bundle <ArrowRight size={14} />
          </button>
          <button type="button" onClick={() => { close(); setTimeout(() => go(card), 150); }} data-testid="quiz-see-toolkit"
            className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-neutral-200 hover:border-white/40">See the toolkit</button>
        </div>
      </div>
      {wantsTalk && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-5" data-testid="quiz-session-rec">
          <div className="text-sm text-amber-100">You also said you'd like someone to talk to.</div>
          <p className="text-xs text-neutral-400 mt-1">A 30-minute, one-on-one Google Meet with Gabi, $45. Peer support, not therapy.</p>
          <button type="button" onClick={() => { close(); navigate("/book"); }} data-testid="quiz-book-session"
            className="mt-3 rounded-full border border-amber-400/50 px-5 py-2 text-sm text-amber-100 hover:bg-amber-500/10">Book a session</button>
        </div>
      )}
      <button type="button" onClick={restart} data-testid="quiz-restart" className="text-xs text-neutral-500 hover:text-neutral-300 inline-flex items-center gap-1.5">
        <RotateCcw size={12} /> Start over
      </button>
    </div>
  );
};

export const StartQuiz = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({});
  const restart = () => { setStep(0); setAns({}); };
  const choose = (k, v) => { setAns({ ...ans, [k]: v }); setStep(step + 1); };

  return (
    <>
      <button type="button" onClick={() => { restart(); setOpen(true); }} data-testid="quiz-open"
        className="mt-5 text-sm text-teal-300 hover:text-teal-200 underline underline-offset-4 decoration-teal-400/40">
        Not sure where to start? Take the 3-question quiz
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-[#0b1418] border-[#1d3a3a] max-h-[90vh] overflow-y-auto" data-testid="quiz-dialog">
          <DialogTitle className="font-display text-2xl text-neutral-100">{step < 3 ? Q[step].q : "Your starting point"}</DialogTitle>
          <DialogDescription className="text-xs text-neutral-500">
            {step < 3 ? `Question ${step + 1} of 3 · there are no wrong answers` : "Based on what you shared. You can always change course."}
          </DialogDescription>
          {step < 3 && (
            <div className="h-1 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-teal-400 transition-[width] duration-300" style={{ width: `${((step + 1) / 3) * 100}%` }} />
            </div>
          )}
          {step < 3 ? (
            <div className="space-y-2" data-testid={`quiz-step-${step + 1}`}>
              {Q[step].a.map(([v, l]) => (
                <button key={v} type="button" onClick={() => choose(Q[step].key, v)} data-testid={`quiz-answer-${Q[step].key}-${v}`}
                  className="w-full text-left rounded-xl border border-white/10 hover:border-teal-400/60 hover:bg-teal-500/5 px-4 py-3.5 text-sm text-neutral-200 transition-colors">
                  {l}
                </button>
              ))}
              {step > 0 && (
                <button type="button" onClick={() => setStep(step - 1)} data-testid="quiz-back" className="text-xs text-neutral-500 hover:text-neutral-300 inline-flex items-center gap-1.5 pt-2">
                  <ArrowLeft size={12} /> Back
                </button>
              )}
              {ans.where === "unsafe" && step === 2 && <Crisis />}
            </div>
          ) : (
            <Result ans={ans} close={() => setOpen(false)} restart={restart} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
